import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import axios from '../config/api';

export default function DeleteAccount() {
  const [step, setStep] = useState<'info' | 'confirm' | 'done'>('info');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const { user, clearAuth } = useAuthStore();
  const navigate = useNavigate();

  const handleDelete = async () => {
    if (confirmText !== 'ELIMINAR') return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.delete('/auth/delete-account');
      if (res.data.success) {
        setStep('done');
        setTimeout(() => {
          clearAuth();
          navigate('/login');
        }, 3000);
      } else {
        setError(res.data.message || 'Error al eliminar la cuenta.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al procesar la solicitud. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'done') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="text-5xl mb-4">✅</div>
          <h1 className="text-2xl font-bold mb-2">Cuenta Eliminada</h1>
          <p className="text-gray-600">Tu cuenta y todos tus datos han sido eliminados. Serás redirigido en unos segundos.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8">
        <button onClick={() => navigate(-1)} className="text-blue-600 hover:text-blue-800 text-sm mb-6 inline-flex items-center gap-1">
          ← Volver
        </button>

        <div className="text-center mb-6">
          <div className="text-5xl mb-3">⚠️</div>
          <h1 className="text-2xl font-bold">Eliminar mi Cuenta</h1>
          <p className="text-gray-500 mt-1">{user?.email}</p>
        </div>

        {step === 'info' && (
          <div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h2 className="font-semibold text-red-800 mb-2">Esta acción es permanente e irreversible</h2>
              <p className="text-red-700 text-sm mb-3">Al eliminar tu cuenta se borrarán:</p>
              <ul className="text-red-700 text-sm space-y-1 ml-4 list-disc">
                <li>Tu perfil y datos personales</li>
                <li>Historial de búsquedas de rutas</li>
                <li>Registros de uso de plataformas</li>
                <li>Comentarios y feedback enviados</li>
                <li>Códigos de verificación asociados</li>
              </ul>
            </div>
            <button
              onClick={() => setStep('confirm')}
              className="w-full py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
            >
              Quiero eliminar mi cuenta
            </button>
            <button
              onClick={() => navigate(-1)}
              className="w-full py-3 mt-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
          </div>
        )}

        {step === 'confirm' && (
          <div>
            <p className="text-gray-700 mb-4">
              Para confirmar, escribe <span className="font-bold text-red-600">ELIMINAR</span> en el campo de abajo:
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Escribe ELIMINAR"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              aria-label="Escribe ELIMINAR para confirmar"
            />
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}
            <button
              onClick={handleDelete}
              disabled={confirmText !== 'ELIMINAR' || loading}
              className="w-full py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Eliminando...' : 'Confirmar Eliminación'}
            </button>
            <button
              onClick={() => { setStep('info'); setConfirmText(''); setError(''); }}
              className="w-full py-3 mt-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
